import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import {
  ShieldCheck, Sparkles, Stethoscope, HeartHandshake, BookOpen, Quote,
  Building2, HandHeart, Activity, ArrowUpRight, Calendar, Phone,
} from "lucide-react";
import useReveal from "@/lib/useReveal";
import { useLang } from "@/lib/i18n";
import { CLINIC } from "@/lib/data";

const MILE_YEARS = ["1846", "1954", "1996", "2005", "2018", ""];

export default function About() {
  const { t, lang } = useLang();

  const milestones = [1, 2, 3, 4, 5, 6].map((i, idx) => ({
    year: i === 6 ? t("about.timeline.6.year") : MILE_YEARS[idx],
    title: t(`about.timeline.${i}.title`),
    text: t(`about.timeline.${i}.text`),
  }));

  const pillars = [
    { icon: ShieldCheck, t: t("about.pillars.1.t"), d: t("about.pillars.1.d") },
    { icon: Sparkles, t: t("about.pillars.2.t"), d: t("about.pillars.2.d") },
    { icon: Stethoscope, t: t("about.pillars.3.t"), d: t("about.pillars.3.d") },
    { icon: HeartHandshake, t: t("about.pillars.4.t"), d: t("about.pillars.4.d") },
  ];

  const r1 = useReveal();
  const r2 = useReveal();
  const r3 = useReveal();
  const r4 = useReveal();

  // German fallback for any untranslated long paragraphs
  const isDE = lang === "de";

  return (
    <div data-testid="about-page" className="overflow-hidden">
      <PageHero
        eyebrow={t("about.eyebrow")}
        title={t("about.title")}
        subtitle={t("about.subtitle")}
        crumbs={[{ label: t("nav.about") }]}
        image="https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/maingate.jpg/:/rs=w:1600,cg:true"
      />

      {/* ==== LINEAGE / FOUNDER ==== */}
      <section className="py-20 sm:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div ref={r1} className="reveal lg:col-span-5 space-y-6 lg:sticky lg:top-32">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold">{t("about.lineage.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[3.4rem] text-deep font-semibold leading-[1.05]">
              {t("about.lineage.title1")} <span className="italic text-brand">{t("about.lineage.title2")}</span>
            </h2>
            <p className="text-ink-soft leading-relaxed">{t("about.lineage.p1")}</p>
            <p className="text-ink-soft leading-relaxed">{t("about.lineage.p2")}</p>

            <div className="mt-6 p-6 bg-brand/5 rounded-2xl border-l-4 border-gold">
              <Quote className="w-5 h-5 text-gold mb-3" strokeWidth={1.6} />
              <p className="font-display text-lg text-deep italic leading-snug">
                {isDE
                  ? "„Im Dienste der Menschheit liegt der Dienst an Gott."
                  : "“Service to humanity is the true service to God."}
              </p>
              <div className="mt-3 text-xs tracking-[0.18em] uppercase text-brand font-bold">— {isDE ? "Leitmotiv der Stiftung" : "Trust's guiding motto"}</div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-7 relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-gold/30 via-brand/15 to-deep/10 rounded-[2rem] blur-2xl" />
                <img src="https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/maplogo.jpg/:/rs=w:1200,cg:true" alt="Shri Anandpur Trust" className="relative rounded-[1.75rem] w-full shadow-lifted border-4 border-white" />
              </div>
              <div className="col-span-12 sm:col-span-5 grid grid-rows-2 gap-4">
                <img src="https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/maingate.jpg/:/rs=w:800,cg:true" alt="Centre entrance" className="rounded-[1.5rem] w-full h-full object-cover shadow-soft border-4 border-white" />
                <img src="https://img1.wsimg.com/isteam/ip/a677c0cf-1124-4ab0-a6c2-e08aa9227c21/darshan.jpg/:/rs=w:800,cg:true" alt="Darshan" className="rounded-[1.5rem] w-full h-full object-cover shadow-soft border-4 border-white" />
              </div>
            </div>

            <div ref={r2} className="reveal reveal-delay-1 prose prose-slate max-w-none mt-8 space-y-5 text-ink-soft leading-relaxed">
              <p>
                <strong className="text-deep">Shri Paramhans Advait Math</strong> made its advent over a hundred years ago to spread and preach Truth, the ideals of Bhakti, Karam-Yoga, and spiritual knowledge. It was founded by the First Great Master, the Prophet of the age,
              </p>
              <p className="italic text-deep">
                PARAMHANS SHRI SHRI 108 SHRI SWAMI ADVAIT ANANDJI MAHARAJ
              </p>
              <p>
                Born in 1846 A.D., he tirelessly worked for the good of humanity and the spiritual upliftment of the common masses. Selfless service to mankind is the motto of this Trust, and that mission has been carried forward by his successors with compassion and dedication.
              </p>
              <p>
                <strong className="text-deep">Shri Anandpur Trust</strong>, established in 1954, is located at <em className="text-deep not-italic font-semibold">Shri Anandpur near Esagarh, Distt. Ashoknagar (MP)</em>. It is an embodiment of spirituality, social service, truth and love, and it adorns the state of Madhya Pradesh by the higher ideals taught by our sages of the ancient past.
              </p>
              <p>
                This Trust has always done its duty towards the nation and risen to the occasion whenever emergencies and natural calamities demanded. The Trust contributes to the <strong className="text-deep">Prime Minister's Relief Fund</strong> and the <strong className="text-deep">Madhya Pradesh Chief Minister's Relief Fund</strong>, and it supports social and relief institutions across the state and beyond.
              </p>
              <p>
                For the past 28 years the Trust has run a modern <strong className="text-deep">150-bed hospital at Sukhpur</strong> on the Esagarh–Ashoknagar Road (MP). The hospital serves a population made up largely of Adivasis, Scheduled Castes, tribal communities and other backward and poor groups, providing pathological and radiological diagnostics alongside modern operating theatres and ICCU care.
              </p>
              <p>
                In keeping with the motto of service to the poor and needy, the Trust has opened high-tech charitable diagnostic centres at <strong className="text-deep">Delhi, Pune and Gwalior</strong>. The Delhi diagnostic centre includes <strong className="text-brand">free dialysis facilities</strong>, adding a new dimension of service to the public.
              </p>
              <p>
                This charitable centre is unique in the metropolitan region, where most state-of-the-art diagnostic facilities are provided under one roof at a very nominal cost. It offers modern diagnostic machines such as <em className="text-deep not-italic font-semibold">MRI, CT Scan, Colour Doppler, Digital X-Ray, Ultrasound, Bone Densitometer, TMT, ECG, ECHO, EEG, Endoscopy, Hemodialysis</em> and a fully automated Path Lab.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==== MISSION QUOTE — full bleed ==== */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-deep" />
        <div className="absolute inset-0 aurora-bg opacity-70" />
        <div className="absolute inset-0 subtle-grain opacity-30" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <BookOpen className="w-12 h-12 text-gold mx-auto mb-7 float-slow" strokeWidth={1.4} />
          <p className="font-display text-2xl sm:text-3xl lg:text-[2.6rem] leading-[1.18]">
            "{t("about.mission.quote")}"
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2 text-xs tracking-[0.25em] uppercase text-gold font-bold">
            <span className="w-10 h-px bg-gold" />
            <span>Shri Paramhans Advait Math</span>
            <span className="w-10 h-px bg-gold" />
          </div>
        </div>
      </section>

      {/* ==== JOURNEY TIMELINE — alternating cards ==== */}
      <section ref={r3} className="reveal py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("about.timeline.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-deep font-semibold tracking-tight">{t("about.timeline.title")}</h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand/30 to-transparent" />
            <div className="space-y-12">
              {milestones.map((m, i) => (
                <div key={i} className={`grid md:grid-cols-2 gap-6 md:gap-14 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`milestone-${i}`}>
                  <div className={`relative md:text-${i % 2 === 1 ? "left" : "right"}`}>
                    <div className="inline-flex flex-col md:items-end">
                      <span className="font-display text-6xl md:text-7xl text-brand font-semibold leading-none">{m.year}</span>
                      <span className="hidden md:block w-12 h-px bg-gold mt-3" />
                    </div>
                    <div className="hidden md:block absolute top-1/2 w-3 h-3 rounded-full bg-gold ring-4 ring-background"
                         style={i % 2 === 1 ? { right: "-6.5rem" } : { left: "-6.5rem" }} />
                  </div>
                  <div className="p-7 bg-white rounded-3xl shadow-soft border border-black/5 hover:shadow-lifted transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                        {[Building2, HandHeart, Stethoscope, Activity, Sparkles, ShieldCheck][i] && React.createElement([Building2, HandHeart, Stethoscope, Activity, Sparkles, ShieldCheck][i], { className: "w-4 h-4", strokeWidth: 1.7 })}
                      </div>
                      <h3 className="font-display text-2xl text-deep font-semibold">{m.title}</h3>
                    </div>
                    <p className="text-ink-soft text-sm leading-relaxed">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==== PILLARS ==== */}
      <section ref={r4} className="reveal py-20 bg-brand/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("about.pillars.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-deep font-semibold">{t("about.pillars.title")}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((it, idx) => (
              <div key={idx} className="tilt-card p-7 rounded-3xl bg-white border border-black/5 shadow-soft" data-testid={`pillar-${idx}`}>
                <it.icon className="w-8 h-8 text-brand mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-xl text-deep font-semibold mb-2">{it.t}</h3>
                <p className="text-sm text-ink-soft">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==== CTA FOOT ==== */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display text-2xl sm:text-3xl text-deep font-semibold mb-3">
            {isDE ? "Ein Anruf genügt — wir helfen Ihnen weiter." : "One call is all it takes — we'll guide you the rest of the way."}
          </h3>
          <p className="text-ink-soft mb-7 max-w-xl mx-auto text-sm">
            {isDE
              ? "Unsere Rezeption hilft Ihnen, die richtige Untersuchung, Zeit und den passenden Facharzt zu wählen."
              : "Our reception helps you choose the right investigation, time and consultant — at a charitable, nominal cost."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href={`tel:${CLINIC.altPhone.replace(/\s|-/g, "")}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand text-white rounded-full font-semibold shadow-soft hover:bg-brand-600 transition-all">
              <Phone className="w-4 h-4" /> {CLINIC.altPhone}
            </a>
            <Link to="/services" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-deep border border-deep/15 rounded-full font-semibold">
              {isDE ? "Einrichtung erkunden" : "Explore Facility"} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
